import { useEffect, useRef, useState, useCallback } from 'react';
import { usePrivy } from '@privy-io/react-auth';
import { useAuth } from '../contexts/AuthContext';
import { useGeolocation } from './useGeolocation';
import { useAppForeground } from './useAppForeground';
import { useModelPreferences } from './useModelPreferences';
import { getFeatureFlags } from '../config/feature-flags';
import { DEFAULT_REFRESH_CONFIG } from '../config/refresh-config';
import { detectMissionIntent } from '../workers/intent-capture';
import { resolveMissionWorkerRoutePlan } from '../lib/llm-routing';
import {
  publishMissionSpineMarker,
} from '../lib/mission-spine/instrumentation';
import {
  OWNYOU_DATA_CHANGED_EVENT,
  forwardOwnYouDataChangedToWorker,
} from '../lib/mission-spine/data-change-bridge';
import type { TriggerEngineStats, TriggerResult } from '@ownyou/triggers';
import type { MissionCard } from '@ownyou/shared-types';
import type {
  WorkerInboundMessage,
  WorkerOutboundMessage,
  AgentExecutionProgress,
  ExecutionResult,
  EnabledAgents,
} from '../workers/worker-protocol';
import type { ChangeAlert } from '../workers/change-detector';

// Re-export for backward compatibility
export type WorkerMessage = WorkerInboundMessage;

// Sprint 17g-4: Response type mapping for postRawMessage (module-scope, M4)
const RESPONSE_TYPES: Record<string, { success: string; error: string }> = {
  GENERATE_SUMMARY: { success: 'SUMMARY_RESULT', error: 'SUMMARY_ERROR' },
};

const AGENT_REFRESH_LS_KEY = 'ownyou_last_agent_refresh';
const AGENT_REFRESH_BUDGET_LS_KEY = 'ownyou_agent_refresh_budget_date';
const AGENT_REFRESH_COUNT_LS_KEY = 'ownyou_agent_refresh_count';

export interface UseAgentWorkerOptions {
  /** Called when pipeline refresh is needed (> 4h since last run) */
  onPipelineRefreshNeeded?: () => void;
  /** Called when missions may have changed (housekeeping/refresh completed) */
  onMissionsChanged?: () => void;
  /** Called when a change alert is detected (price drop, deadline, etc.) */
  onChangeAlert?: (alert: ChangeAlert) => void;
}

export function useAgentWorker(options?: UseAgentWorkerOptions) {
  const { wallet, isAuthenticated } = useAuth();
  const { getAccessToken } = usePrivy();
  const { position } = useGeolocation();
  const { routeSelections, models } = useModelPreferences();
  const userId = wallet?.address ?? 'anonymous';
  const onPipelineRefreshNeededRef = useRef(options?.onPipelineRefreshNeeded);
  onPipelineRefreshNeededRef.current = options?.onPipelineRefreshNeeded;
  const onMissionsChangedRef = useRef(options?.onMissionsChanged);
  onMissionsChangedRef.current = options?.onMissionsChanged;
  const onChangeAlertRef = useRef(options?.onChangeAlert);
  onChangeAlertRef.current = options?.onChangeAlert;

  const workerRef = useRef<Worker | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [stats, setStats] = useState<TriggerEngineStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Sprint 15: Mission pipeline state
  const [isExecuting, setIsExecuting] = useState(false);
  const [recentMissions, setRecentMissions] = useState<MissionCard[]>([]);
  const [executionProgress, setExecutionProgress] = useState<AgentExecutionProgress | null>(null);
  const [lastExecutionResult, setLastExecutionResult] = useState<ExecutionResult | null>(null);

  // Request promise map for request-response pattern
  const requestMapRef = useRef<Map<string, (result: TriggerResult) => void>>(new Map());

  useEffect(() => {
    if (!isAuthenticated) return;

    // Initialize worker
    // Vite syntax for worker import
    const worker = new Worker(new URL('../workers/agent.worker.ts', import.meta.url), {
      type: 'module',
    });

    worker.onmessage = (event: MessageEvent<WorkerOutboundMessage>) => {
      const { type, payload } = event.data as any;

      switch (type) {
        case 'INIT_COMPLETE':
          setIsReady(true);
          // Auto-start after init
          worker.postMessage({ type: 'START' });
          break;

        case 'STARTED':
          setIsRunning(true);
          break;

        case 'STOPPED':
          setIsRunning(false);
          break;

        case 'STATS_UPDATE':
          setStats(payload);
          break;

        case 'REQUEST_COMPLETE': {
          const resolver = requestMapRef.current.get(payload.requestId);
          if (resolver) {
            resolver(payload.result);
            requestMapRef.current.delete(payload.requestId);
          }
          break;
        }

        // Sprint 15: Mission pipeline messages
        case 'EXECUTION_STARTED':
          setIsExecuting(true);
          setExecutionProgress({
            total: payload.agentCount,
            completed: 0,
            currentAgent: null,
            agents: new Map(),
          });
          break;

        case 'AGENT_PROGRESS':
          setExecutionProgress(prev => {
            if (!prev) return prev;
            const agents = new Map(prev.agents);
            agents.set(payload.agentType, payload.status);
            const completed = Array.from(agents.values()).filter(
              s => s === 'complete' || s === 'error' || s === 'skipped',
            ).length;
            return {
              ...prev,
              completed,
              currentAgent: payload.status === 'running' ? payload.agentType : prev.currentAgent,
              agents,
            };
          });
          break;

        case 'MISSIONS_GENERATED':
          setRecentMissions(prev => [...prev, ...payload.missions].slice(-100));
          break;

        case 'EXECUTION_COMPLETE':
          setIsExecuting(false);
          setLastExecutionResult({
            totalMissions: payload.totalMissions,
            durationMs: payload.durationMs,
            timestamp: Date.now(),
          });
          break;

        case 'EXECUTION_ERROR':
          setIsExecuting(false);
          setError(payload.error);
          console.error('[AgentWorker] Execution error:', payload.error);
          break;

        // Sprint 17e: Refresh system responses
        case 'HOUSEKEEPING_COMPLETE':
          console.log('[AgentWorker] Housekeeping complete:', payload);
          onMissionsChangedRef.current?.();
          break;

        case 'REFRESH_COMPLETE':
          console.log('[AgentWorker] Refresh complete:', payload);
          onMissionsChangedRef.current?.();
          break;

        // Sprint 17g-3: Change detection alerts
        case 'CHANGE_ALERT':
          onChangeAlertRef.current?.(payload as ChangeAlert);
          break;

        case 'ERROR':
          console.error('[AgentWorker Error]', payload.error);
          setError(payload.error);
          break;

        case 'MISSION_ROUTE_TELEMETRY':
          console.info('[AgentWorker] Mission route telemetry:', payload.event);
          break;

        case 'MISSION_SPINE_MARKER':
          publishMissionSpineMarker(payload, { log: true, capture: true, dispatch: true });
          break;
      }
    };

    workerRef.current = worker;

    // Build enabled agents map from feature flags
    const flags = getFeatureFlags();
    const enabledAgents: EnabledAgents = {
      shopping: flags.enableShoppingAgent,
      restaurant: flags.enableRestaurantAgent,
      travel: flags.enableTravelAgent,
      events: flags.enableEventsAgent,
      content: flags.enableContentAgent,
      diagnostic: flags.enableDiagnosticAgent,
    };

    let cancelled = false;

    const sendInit = async () => {
      let llmProxyToken: string | null = null;
      try {
        llmProxyToken = await getAccessToken();
      } catch {
        llmProxyToken = null;
      }
      if (cancelled) return;

      const missionRoutePlan = resolveMissionWorkerRoutePlan({
        routeSelections,
        models,
        authToken: llmProxyToken,
      });

      worker.postMessage({
        type: 'INIT',
        payload: {
          userId,
          walletAddress: wallet?.address,
          apiKeys: {
            llmProxyToken: llmProxyToken ?? undefined,
          },
          missionRoutePlan,
          enabledAgents,
          geolocation: (() => {
            try {
              const cached = localStorage.getItem('ownyou_geolocation');
              if (cached) {
                const parsed = JSON.parse(cached);
                if (Date.now() - parsed.timestamp < 15 * 60 * 1000) {
                  return parsed.position as { latitude: number; longitude: number };
                }
              }
            } catch { /* ignore */ }
            return undefined;
          })(),
        }
      });
    };

    void sendInit();

    // Listen for data change events from DataSourceContext (cross-context bridge)
    const handleDataChange = (e: Event) => {
      forwardOwnYouDataChangedToWorker(e, worker);
    };
    window.addEventListener(OWNYOU_DATA_CHANGED_EVENT, handleDataChange);

    return () => {
      cancelled = true;
      window.removeEventListener(OWNYOU_DATA_CHANGED_EVENT, handleDataChange);
      worker.terminate();
      workerRef.current = null;
    };
  }, [isAuthenticated, userId, wallet, routeSelections, models, getAccessToken]);

  // Send live geolocation updates to worker (for restaurant/events agents)
  useEffect(() => {
    if (position && workerRef.current) {
      workerRef.current.postMessage({
        type: 'UPDATE_GEOLOCATION',
        payload: { latitude: position.latitude, longitude: position.longitude },
      });
    }
  }, [position]);

  // Sprint 17e: Foreground cascade — housekeeping, agent refresh, pipeline refresh
  useAppForeground({
    minBackgroundMs: DEFAULT_REFRESH_CONFIG.minBackgroundDurationMs,
    enabled: isReady,
    onForeground: () => {
      // Tier 1: Always run housekeeping
      workerRef.current?.postMessage({ type: 'RUN_HOUSEKEEPING' });

      // Tier 2: Agent refresh if interval exceeded and budget not exhausted
      const lastRefresh = localStorage.getItem(AGENT_REFRESH_LS_KEY);
      const lastRefreshTime = lastRefresh ? parseInt(lastRefresh, 10) : 0;
      const timeSinceRefresh = Date.now() - lastRefreshTime;

      // Check daily budget
      const budgetDate = localStorage.getItem(AGENT_REFRESH_BUDGET_LS_KEY);
      const today = new Date().toISOString().slice(0, 10);
      let refreshCount = 0;
      if (budgetDate === today) {
        refreshCount = parseInt(localStorage.getItem(AGENT_REFRESH_COUNT_LS_KEY) ?? '0', 10);
      }

      // Use shortest agent refresh interval as the threshold
      const minInterval = Math.min(...Object.values(DEFAULT_REFRESH_CONFIG.agentRefreshInterval));
      if (timeSinceRefresh > minInterval && refreshCount < DEFAULT_REFRESH_CONFIG.dailyAgentBudget) {
        workerRef.current?.postMessage({ type: 'REFRESH_AGENTS' });
        localStorage.setItem(AGENT_REFRESH_LS_KEY, String(Date.now()));
        localStorage.setItem(AGENT_REFRESH_BUDGET_LS_KEY, today);
        localStorage.setItem(AGENT_REFRESH_COUNT_LS_KEY, String(refreshCount + 1));
      }

      // Tier 3: Pipeline refresh if > 4h
      if (timeSinceRefresh > DEFAULT_REFRESH_CONFIG.pipelineRefreshGuardMs) {
        onPipelineRefreshNeededRef.current?.();
      }
    },
  });

  const postRawMessage = useCallback((msg: WorkerInboundMessage): Promise<WorkerOutboundMessage> => {
    return new Promise((resolve, reject) => {
      if (!workerRef.current) {
        reject(new Error('Agent worker not ready'));
        return;
      }
      const responseTypes = RESPONSE_TYPES[msg.type];
      if (!responseTypes) {
        reject(new Error(`No response mapping for message type: ${msg.type}`));
        return;
      }

      const requestId = `${msg.type}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      const handler = (e: MessageEvent) => {
        if ((e.data.type === responseTypes.success || e.data.type === responseTypes.error) &&
            e.data.requestId === requestId) {
          workerRef.current?.removeEventListener('message', handler);
          clearTimeout(timer);
          if (e.data.type === responseTypes.error) {
            reject(new Error(e.data.error));
          } else {
            resolve(e.data);
          }
        }
      };
      workerRef.current.addEventListener('message', handler);
      workerRef.current.postMessage({ ...msg, requestId });
      const timer = setTimeout(() => {
        workerRef.current?.removeEventListener('message', handler);
        reject(new Error('Worker message timed out'));
      }, 30000);
    });
  }, []);

  const handleUserRequest = useCallback((request: string): Promise<TriggerResult> => {
    return new Promise((resolve, reject) => {
      if (!workerRef.current) {
        reject(new Error('Worker not initialized'));
        return;
      }

      // Sprint 17f Task 3.9: Route mission intents to CREATE_MISSION
      if (detectMissionIntent(request)) {
        workerRef.current.postMessage({
          type: 'CREATE_MISSION',
          payload: { intent: request },
        });
        resolve({ type: 'mission_intent', message: 'Creating missions...' } as unknown as TriggerResult);
        return;
      }

      const requestId = crypto.randomUUID();
      requestMapRef.current.set(requestId, resolve);

      workerRef.current.postMessage({
        type: 'HANDLE_REQUEST',
        payload: { request, requestId }
      });

      // Timeout safety
      setTimeout(() => {
        if (requestMapRef.current.has(requestId)) {
          requestMapRef.current.delete(requestId);
          reject(new Error('Request timed out'));
        }
      }, 30000);
    });
  }, []);

  const start = useCallback(() => {
    workerRef.current?.postMessage({ type: 'START' });
  }, []);

  const stop = useCallback(() => {
    workerRef.current?.postMessage({ type: 'STOP' });
  }, []);

  return {
    isReady,
    isRunning,
    stats,
    error,
    handleUserRequest,
    start,
    stop,
    // Sprint 15: Mission pipeline state
    isExecuting,
    recentMissions,
    executionProgress,
    lastExecutionResult,
    // Sprint 17g-4: Raw message posting
    postRawMessage,
    // Getter for raw worker access (useMissionRefiner needs direct addEventListener)
    getWorker: useCallback(() => workerRef.current, []),
  };
}
